// tslint:disable:no-any
// tslint:disable:no-unsafe-any

import { AppInsightsService, SeverityLevel } from '@bizappframework/ng-application-insights';
import { Logger, LogLevel } from '@bizappframework/ng-logging';

/**
 * Application Insights logger
 */
export class AppInsightsLogger implements Logger {
    private readonly _filter: (category: string, logLevel: LogLevel) => boolean;

    constructor(private readonly _appInsightsService: AppInsightsService,
        readonly name: string,
        filter?: (category: string, logLevel: LogLevel) => boolean) {
        if (filter) {
            this._filter = filter;
        } else {
            this._filter = () => true;
        }
    }

    log(logLevel: LogLevel, message?: string | Error, ...optionalParams: any[]): void {
        if (!this.isEnabled(logLevel)) {
            return;
        }

        if (logLevel === LogLevel.Error || logLevel === LogLevel.Critical) {
            if (typeof message === 'string') {
                this._appInsightsService.trackTrace(message,
                    this.mapToProperties(...optionalParams),
                    logLevel === LogLevel.Critical ? SeverityLevel.Critical : SeverityLevel.Error);
            } else {
                this._appInsightsService.trackException(message as Error,
                    undefined,
                    this.mapToProperties(...optionalParams),
                    undefined,
                    logLevel === LogLevel.Critical ? SeverityLevel.Critical : SeverityLevel.Error);
            }
        } else {
            let msgStr = '';
            if (message && typeof message === 'string') {
                msgStr = message;
            } else {
                msgStr = JSON.stringify(message);
            }

            let severityLevel: SeverityLevel;
            switch (logLevel) {
                case LogLevel.Info: {
                    severityLevel = SeverityLevel.Information;
                    break;
                }
                case LogLevel.Warn: {
                    severityLevel = SeverityLevel.Warning;
                    break;
                }
                default: {
                    severityLevel = SeverityLevel.Verbose;
                }
            }

            this._appInsightsService.trackTrace(msgStr,
                this.mapToProperties(...optionalParams),
                severityLevel);
        }
    }

    trace(message?: string, ...optionalParams: any[]): void {
        this.log(LogLevel.Trace, message, ...optionalParams);
    }

    debug(message: string, ...optionalParams: any[]): void {
        this.log(LogLevel.Debug, message, ...optionalParams);
    }

    info(message: string, ...optionalParams: any[]): void {
        this.log(LogLevel.Info, message, ...optionalParams);
    }

    warn(message: string, ...optionalParams: any[]): void {
        this.log(LogLevel.Warn, message, ...optionalParams);
    }

    error(message: string | Error, ...optionalParams: any[]): void {
        this.log(LogLevel.Error, message, ...optionalParams);
    }

    isEnabled(logLevel: LogLevel): boolean {
        if (logLevel === LogLevel.None || !this._appInsightsService) {
            return false;
        }

        return this._filter(this.name, logLevel);
    }

    private mapToProperties(...optionalParams: any[]): { [name: string]: string } | undefined {
        let properties: { [name: string]: string } | undefined;
        if (optionalParams && optionalParams.length && typeof optionalParams[0] === 'object') {
            properties = optionalParams[0];
        }

        return properties || undefined;
    }
}
